import { useQuery } from '@tanstack/react-query';
import _, { camelCase } from 'lodash';
import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { FieldSectionType } from 'api/field-sections/types';
import { FieldsAPI } from 'api/fields/fields';
import { ObjectType } from 'api/pushes';
import { FieldType, SourceType } from 'api/tasks/field-meta';
import { useEpicFieldsQuery, useSprintFieldsQuery } from 'queries';
import { useAccountConfigQuery } from 'queries/account';
import { useTaskFieldsBaseQuery } from 'queries/fields';
import { FieldsStore } from 'stores/fields';
import { byFieldName } from 'utils/by-field-name';
const selectFields = ({ taskFields })=>taskFields;
const fieldsStore = ({ fields })=>fields;
const fieldNamesMaponDemand = {};
/**
 *
 * @deprecated Use `FieldsStore` instead
 */ const useAccountFields = ()=>useSelector(selectFields, shallowEqual);
const useFieldsStore = ()=>useSelector(fieldsStore, shallowEqual);
export const useFetchFieldByName = (fieldName, objectType)=>{
    const query = useQuery({
        queryKey: [
            'field',
            fieldName,
            objectType
        ],
        queryFn: async ()=>{
            const resp = await FieldsAPI.getPaginatedFields(objectType, {
                includedFieldNames: [
                    fieldName
                ],
                limit: 1
            });
            return resp.data[0];
        }
    });
    return query;
};
export const useFetchMultipleFieldsByName = (fieldNames, objectType)=>{
    const query = useQuery({
        queryKey: [
            'field',
            ...fieldNames !== null && fieldNames !== void 0 ? fieldNames : [],
            objectType
        ],
        queryFn: async ()=>{
            const resp = await FieldsAPI.getPaginatedFields(objectType, {
                includedFieldNames: fieldNames,
                limit: fieldNames.length
            });
            return resp.data;
        },
        enabled: fieldNames.length > 0
    });
    return query;
};
const useBillableCategoryEffortField = ()=>{
    const { data: fields } = useFetchMultipleFieldsByName([
        'defaultTimeEntryBillable',
        'defaultTimeEntryCategory',
        'effort',
        'priority'
    ], ObjectType.Task);
    return _.keyBy(fields !== null && fields !== void 0 ? fields : [], (e)=>camelCase(e.fieldName));
};
/**
 *
 * @deprecated Use `FieldsStore` instead
 *
 */ export const useAccountField = (fieldName)=>{
    const { fields } = useAccountFields();
    return fields.find((f)=>byFieldName(f, fieldName));
};
/**
 * Using `FieldsStore.getTaskFieldByName`
 */ export const useTaskField = (fieldName)=>{
    const store = useFieldsStore();
    const fieldStore = useMemo(()=>FieldsStore.getTaskFieldByName(fieldName)(store), [
        store,
        fieldName
    ]);
    return fieldStore[1];
};
const fieldNameLookup = {};
export const useTaskFieldV2 = (fieldName)=>{
    const fieldsData = useTaskFieldsBaseQuery();
    return useMemo(()=>{
        var _fieldsData_data;
        const name = fieldName === null || fieldName === void 0 ? void 0 : fieldName.toLowerCase();
        if (fieldNameLookup[name]) {
            return fieldNameLookup[name];
        }
        const f = fieldsData === null || fieldsData === void 0 ? void 0 : (_fieldsData_data = fieldsData.data) === null || _fieldsData_data === void 0 ? void 0 : _fieldsData_data.find((field)=>{
            var _field_fieldName;
            return ((_field_fieldName = field.fieldName) === null || _field_fieldName === void 0 ? void 0 : _field_fieldName.toLowerCase()) === name;
        });
        if (f) {
            fieldNameLookup[name] = f;
        }
        return f;
    }, [
        fieldsData,
        fieldName
    ]);
};
export const PRIORITY_FIELD_NAME = 'priority';
const useBCPByName = (fieldName)=>{
    const billableCategoryEffortField = useBillableCategoryEffortField();
    const field = useMemo(()=>{
        if (fieldNamesMaponDemand[fieldName]) {
            return fieldNamesMaponDemand[fieldName];
        }
        if (billableCategoryEffortField) {
            const f = billableCategoryEffortField[fieldName];
            if (f) {
                fieldNamesMaponDemand[fieldName] = f;
                return f;
            }
        }
        return undefined;
    }, [
        billableCategoryEffortField,
        fieldName
    ]);
    return field;
};
export const usePriorityField = ()=>{
    return useBCPByName(PRIORITY_FIELD_NAME);
};
const EFFORT_FIELD_NAME = 'effort';
export const useEffortField = ()=>{
    return useBCPByName(EFFORT_FIELD_NAME);
};
const CATEGORY_FIELD_NAME = 'defaultTimeEntryCategory';
export const useCategoryField = ()=>{
    const field = useBCPByName(CATEGORY_FIELD_NAME);
    return useMemo(()=>{
        var _field_metaData;
        if (!(field === null || field === void 0 ? void 0 : (_field_metaData = field.metaData) === null || _field_metaData === void 0 ? void 0 : _field_metaData.options)) {
            return field;
        }
        return {
            ...field,
            metaData: {
                ...field.metaData,
                options: [
                    ...field.metaData.options
                ].sort((a, b)=>{
                    var _a_label, _b_label;
                    return ((_a_label = a.label) !== null && _a_label !== void 0 ? _a_label : '').localeCompare((_b_label = b.label) !== null && _b_label !== void 0 ? _b_label : '');
                })
            }
        };
    }, [
        field
    ]);
};
const BILLABLE_FIELD_NAME = 'defaultTimeEntryBillable';
export const useBillableField = ()=>{
    return useBCPByName(BILLABLE_FIELD_NAME);
};
export const usePriorityFieldOption = (value)=>{
    var _ref;
    var _priority_metaData;
    const priority = usePriorityField();
    return priority && ((_ref = priority === null || priority === void 0 ? void 0 : (_priority_metaData = priority.metaData) === null || _priority_metaData === void 0 ? void 0 : _priority_metaData.options) !== null && _ref !== void 0 ? _ref : []).find((o)=>o.value === value);
};
export const useSelectedPriority = (fields)=>{
    const priority = usePriorityField();
    const field = fields.find((f)=>f.fieldId === (priority === null || priority === void 0 ? void 0 : priority.fieldId));
    return usePriorityFieldOption(field === null || field === void 0 ? void 0 : field.fieldValue);
};
export const STATUS_FIELD_NAME = 'Status';
export const STATUS_FIELD_QUERY_KEY = 'STATUS_FIELD';
export const useStatusFieldV2 = ()=>{
    const { data: config } = useAccountConfigQuery();
    const field = useMemo(()=>{
        var _config_fields;
        const sf = config === null || config === void 0 ? void 0 : (_config_fields = config.fields) === null || _config_fields === void 0 ? void 0 : _config_fields.find((f)=>f.fieldName === STATUS_FIELD_NAME && (f === null || f === void 0 ? void 0 : f.objectType) === FieldSectionType.Task);
        return {
            ...sf,
            type: FieldType.SingleSelect,
            sourceType: SourceType.Task
        };
    }, [
        config
    ]);
    return field;
};
/**
 *
 * @deprecated Use `useStatusFieldV2` instead
 *
 */ export const useStatusField = ()=>{
    return useStatusFieldV2();
};
export const useStatusFieldValue = (value)=>{
    var _ref;
    var _status_metaData;
    const status = useStatusField();
    const statusField = ((_ref = status === null || status === void 0 ? void 0 : (_status_metaData = status.metaData) === null || _status_metaData === void 0 ? void 0 : _status_metaData.options) !== null && _ref !== void 0 ? _ref : []).find((o)=>o.value === value);
    return {
        ...status,
        fieldValue: value,
        metaFieldValue: statusField
    };
};
export const useTaskFieldByName = (fieldName)=>{
    const field = useTaskField(fieldName);
    if (!field) {
        throw new Error('Task Field is missing ');
    }
    return field;
};
export const useSelectedStatusOption = (fields)=>{
    var _status_metaData_options, _status_metaData;
    const status = useStatusField();
    if (!fields || !status) {
        return undefined;
    }
    const field = fields.find((f)=>(f === null || f === void 0 ? void 0 : f.fieldId) === (status === null || status === void 0 ? void 0 : status.fieldId));
    const selectedOption = field && (status === null || status === void 0 ? void 0 : (_status_metaData = status.metaData) === null || _status_metaData === void 0 ? void 0 : (_status_metaData_options = _status_metaData.options) === null || _status_metaData_options === void 0 ? void 0 : _status_metaData_options.find((o)=>o.value === field.fieldValue));
    return selectedOption ? selectedOption : undefined;
};
export const useProjectPhaseField = ()=>{
    const { data: config } = useAccountConfigQuery();
    const field = useMemo(()=>{
        var _config_fields;
        return config === null || config === void 0 ? void 0 : (_config_fields = config.fields) === null || _config_fields === void 0 ? void 0 : _config_fields.find((f)=>f.fieldName === STATUS_FIELD_NAME && (f === null || f === void 0 ? void 0 : f.objectType) === FieldSectionType.ProjectPhase);
    }, [
        config
    ]);
    return field;
};
export const useProjectStatusField = ()=>{
    const { data: config } = useAccountConfigQuery();
    const field = useMemo(()=>{
        var _config_fields;
        return config === null || config === void 0 ? void 0 : (_config_fields = config.fields) === null || _config_fields === void 0 ? void 0 : _config_fields.find((f)=>f.fieldName === STATUS_FIELD_NAME && (f === null || f === void 0 ? void 0 : f.objectType) === FieldSectionType.Project);
    }, [
        config
    ]);
    return field;
};
export const useProjectPhaseStatusField = ()=>{
    return useProjectPhaseField();
};
export const useSprintStatusField = ()=>{
    const { data: sprintfields } = useSprintFieldsQuery();
    return useMemo(()=>{
        const statusField = sprintfields === null || sprintfields === void 0 ? void 0 : sprintfields.find((field)=>field.fieldName === 'Status');
        if (!statusField) {
            return undefined;
        }
        return {
            ...statusField,
            type: FieldType.SingleSelect,
            sourceType: SourceType.Sprint,
            fieldName: camelCase(statusField.fieldName)
        };
    }, [
        sprintfields
    ]);
};
export const useEpicStatusField = ()=>{
    const { data: epicFields } = useEpicFieldsQuery();
    return useMemo(()=>{
        const statusField = epicFields === null || epicFields === void 0 ? void 0 : epicFields.find((field)=>field.fieldName === 'Status');
        if (!statusField) {
            return undefined;
        }
        return {
            ...statusField,
            type: FieldType.SingleSelect,
            sourceType: SourceType.Epic,
            fieldName: camelCase(statusField.fieldName)
        };
    }, [
        epicFields
    ]);
};
